import AddIcon from "@mui/icons-material/Add";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import { Box, Button } from "@mui/material";
import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import LabeledSwitch from "../../../components/LabeledSwitch";
import CustomAutocomplete from "../../../components/CustomAutocomplete";
import { FilterSelect } from "../../../components/FilterSelect";
import Flex from "../../../components/Flex";
import SearchInput from "../../../components/SearchInput";
import ToggleTabs, { ToggleTabItem } from "../../../components/ToggleTabs";
import CustomTabs from "../../../components/app/CustomTabs";
import UpsertProviderUserDrawer from "../../../components/app/admin/provider-groups/ProviderUserDrawer";
import UpsertStaffUserDrawer from "../../../components/app/admin/provider-groups/UpsertStaffUserDrawer";
import UpsertRoleModal from "../../../components/app/settings/user-settings/UpsertRoleModal";
import {
  PROVIDER_USER_INITIAL_VALUES,
  ProviderUserType,
} from "../../../constants/ProviderUserSchema";
import { PROVIDER_ROLE_LIST } from "../../../constants/master";
import { appPaths } from "../../../constants/paths";
import { StaffUser } from "../../../constants/staff-user-schema";
import { useDebounce } from "../../../hooks/use-debounce";
import { useGetCosigners } from "../../../queries/provider/use-get-cosigners";
import useDisclosure from "../../../hooks/use-disclosure";
import { useReduceDisclosure } from "../../../hooks/use-reduce-disclosure";
import { CurView } from "../../../pages/app/admin/tenant-settings/tenant-providers";
import { useGetProviderGroupProfile } from "../../../queries/admin/provider-group/use-get-provider-group-profile";
import { useAddProviderUser } from "../../../queries/use-add-provider-user";
import { useAddStaffUser } from "../../../queries/use-add-staff-user";
import {
  DEFAULT_UUID_NAME_LIST,
  getUnNecessaryTransformData,
  transformMultiSelectFrontEndVal,
  DEF_PAGINATION,
  getXtenantId,
} from "../../../utils/utils";
import StyledButtonV2 from "../../../components/app/StyledButtonV2";
import { getViewAs, getStoredRealm, getBaseRole } from "@/utils/auth-storage";

type UsersButtonDataType = {
  path: string;
  value: string;
  label: string;
};

const VIEW_TOGGLE_TABS: ToggleTabItem<CurView>[] = [
  {
    value: "table_view",
    icon: <ViewListOutlinedIcon sx={{ fontSize: 18 }} />,
  },
  {
    value: "card_view",
    icon: <GridViewOutlinedIcon sx={{ fontSize: 18 }} />,
  },
];

const UserSettingsLayout = () => {
  const [addProviderUserDrawerDisclosure, addStaffUserDrawerDisclosure] =
    useReduceDisclosure(2);
  const [curView, setCurView] = useState<CurView>("table_view");
  const { mutateAsync: addProvider } = useAddProviderUser();
  const { mutateAsync: addStaff } = useAddStaffUser();
  const xTENANTID = getStoredRealm();
  const viewAs = getViewAs();
  const [isArchieved, setIsArcheived] = useState(false);

  const { data: profileData } = useGetProviderGroupProfile(
    xTENANTID ?? undefined,
    viewAs !== "provider"
  );
  const {
    opened: openedViewRoleModel,
    open: openViewRoleModel,
    close: closeViewRoleModel,
  } = useDisclosure(false);

  function addStaffUser(values: StaffUser) {
    return addStaff({
      user: getUnNecessaryTransformData(["workLocations"], values) as any,
      xTENANTID: xTENANTID ?? undefined,
    });
  }

  function addProviderUser(values: ProviderUserType) {
    const transformedData: any = getUnNecessaryTransformData(
      ["acceptedInsurances", "licensedStates", "workLocations", "specialities"],
      values
    );

    return addProvider(transformedData);
  }

  const ButtonsData: UsersButtonDataType[] = [
    {
      path:
        viewAs === "provider"
          ? appPaths.staffUsersPagePath
          : appPaths.adminUsersPagePath,
      value: "usersButton",
      label: "Staffs",
    },
    {
      path: appPaths.providerUsersPagePath,
      value: "providers",
      label: "Providers",
    },
    {
      path:
        viewAs === "provider"
          ? appPaths.providerRoleAndResponsibilityPath
          : appPaths.adminRoleAndResponsibilityPath,
      value: "rolesAndResponsibilityButton",
      label: "Roles & Responsibility",
    },
  ];

  const isPracticeAdmin = getBaseRole() === "PRACTICE_ADMIN";

  const location = useLocation();
  const navigate = useNavigate();
  const [searchKey, setSearchKey, instantSearchVal] = useDebounce<string>("");
  const [statusKey, setStatusKey] = useState<string | null>(null);
  const [cosignerKey, setCosignerKey] = useState<string | null>(null);
  const { data: cosignerList } = useGetCosigners({
    pagination: { ...DEF_PAGINATION, pageSize: 50 },
    xTENANTID: getXtenantId(),
    enabled: location.pathname === appPaths.providerUsersPagePath,
  });
  const cosignerOptions =
    cosignerList?.data?.content?.map((it) => ({
      value: it.uuid,
      label: `${it.firstName ?? ""} ${it.lastName ?? ""}`.trim(),
    })) ?? [];
  const [activeButton, setActiveButton] = useState<UsersButtonDataType>(
    !isPracticeAdmin
      ? ButtonsData[0]
      : (ButtonsData.find((ele) => ele.path === location.pathname) ??
          ButtonsData[0])
  );

  return (
    <Box>
      <Flex
        justifyContent={"space-between"}
        alignItems={"center"}
        height={{
          sm: "100px",
          md: "60px",
          lg: "48px",
        }}
        position="fixed"
        top="60px"
        left="15px"
        right="15px"
        zIndex="1000"
        flexWrap={{
          sm: "wrap",
          md: "wrap",
          lg: "nowrap",
        }}
        padding="10px"
        sx={{ background: "#fff" }}
        // width={{
        //   sm: "84%",
        //   md: "95%",
        //   lg: "97%",
        // }}
      >
        <Box>
          <CustomTabs
            value={activeButton.value}
            tabsData={ButtonsData?.filter((ele) =>
              !isPracticeAdmin
                ? ele.value !== "rolesAndResponsibilityButton"
                : true
            )}
            onChange={(data) => {
              const ActiveButtonData =
                ButtonsData.find((value) => value.value === data) ??
                ButtonsData[0];

              navigate(ActiveButtonData.path);
              setActiveButton(ActiveButtonData);
              setIsArcheived(false);
            }}
          />
        </Box>
        {(location.pathname === appPaths.adminUsersPagePath ||
          location.pathname === appPaths.providerUsersPagePath ||
          location.pathname === appPaths.staffUsersPagePath) && (
          <Flex gap={"10px"} alignItems={"center"}>
            <Box>
              <SearchInput
                searchKey={instantSearchVal}
                onSearch={setSearchKey}
                placeholder="Search By Name, Role"
              />
            </Box>
            <Box width={"20%"}>
              <FilterSelect
                value={statusKey}
                options={STATUS_OPTIONS as any}
                placeholder="Status"
                onChange={(val) => setStatusKey(val)}
              />
            </Box>
            {location.pathname === appPaths.providerUsersPagePath && (
              <Box width={"20%"}>
                <FilterSelect
                  value={cosignerKey}
                  options={cosignerOptions as any}
                  placeholder="Co-Signer"
                  onChange={(val) => setCosignerKey(val)}
                />
              </Box>
            )}
            {/* todo future reference
             <UsersModalComponent
              open={openUserModal}
              handleCloseModal={handleCloseModal}
              title="Add New User"
              onSubmit={onSubmit}
            /> */}

            <ToggleTabs<CurView>
              tabs={VIEW_TOGGLE_TABS}
              activeTab={curView}
              onTabChange={setCurView}
              containerSx={{
                border: "none",
                borderBottom: "none",
                backgroundColor: "transparent",
                padding: 0,
              }}
            />

            {location.pathname === appPaths.providerUsersPagePath &&
              isPracticeAdmin && (
                <Flex alignItems={"center"} gap={2} ml={1}>
                  <LabeledSwitch
                    label="Show Archived Providers"
                    checked={isArchieved}
                    onChange={(e) => setIsArcheived(e.target.checked)}
                  />
                  <StyledButtonV2
                    label="Add Provider User"
                    shouldAddIcon
                    sx={{ width: "14rem" }}
                    variant="outlined"
                    onClick={addProviderUserDrawerDisclosure.open}
                  />
                </Flex>
              )}
            {/* todo: need to review from Raj : reason : it is prefilling from outer side. previously it was prefilling from inner side. */}
            {addProviderUserDrawerDisclosure.opened && (
              <UpsertProviderUserDrawer
                open={addProviderUserDrawerDisclosure.opened}
                title="Add Provider User"
                shouldGetPrefillList
                prefillSpecialtyList={transformMultiSelectFrontEndVal(
                  DEFAULT_UUID_NAME_LIST,
                  profileData?.specialities
                )?.map((it) => ({ uuid: it?.uuid, name: it?.name }))}
                onClose={addProviderUserDrawerDisclosure.close}
                onSubmit={addProviderUser}
                prefillValues={{
                  ...(PROVIDER_USER_INITIAL_VALUES ?? {}),
                  timezone: profileData?.timezone,
                  groupNpiNumber: profileData?.npi,
                }}
              />
            )}

            {location.pathname === appPaths.staffUsersPagePath && (
              <Flex alignItems={"center"} gap={2} ml={1}>
                <LabeledSwitch
                  label="Show Archived Staffs"
                  checked={isArchieved}
                  onChange={(e) => setIsArcheived(e.target.checked)}
                />
                <StyledButtonV2
                  label="Add Staff User"
                  sx={{ width: "14rem" }}
                  variant="outlined"
                  shouldAddIcon
                  onClick={addStaffUserDrawerDisclosure.open}
                />
              </Flex>
            )}

            {addStaffUserDrawerDisclosure.opened && (
              <UpsertStaffUserDrawer
                open={addStaffUserDrawerDisclosure.opened}
                modalTitle="Add Staff User"
                onClose={addStaffUserDrawerDisclosure.close}
                onSubmit={addStaffUser}
              />
            )}
          </Flex>
        )}
        {(location.pathname === appPaths.adminRoleAndResponsibilityPath ||
          location.pathname === appPaths.providerRoleAndResponsibilityPath) && (
          <Flex
            justifyContent={viewAs === "provider" ? "end" : "space-between"}
          >
            {location.pathname === appPaths.adminRoleAndResponsibilityPath && (
              <CustomAutocomplete
                label="Search By Role"
                options={PROVIDER_ROLE_LIST}
                placeholder="Select Role"
                flexProps={{
                  flexDirection: "row",
                  alignItems: "center",
                  width: {
                    sm: "300px",
                    md: "400px",
                    lg: "554px",
                  },
                  mt: {
                    sm: "10px",
                  },
                }}
                typographyProps={{
                  width: "200px",
                  mt: "10px",
                  fontWeight: 500,
                }}
              />
            )}

            <Box display={"flex"} gap={1}>
              <UpsertRoleModal
                action={"add"}
                opened={openedViewRoleModel}
                onClose={closeViewRoleModel}
                onSave={() => new Promise(() => {})}
              />

              <LabeledSwitch
                label="Show Archived Roles"
                checked={isArchieved}
                onChange={(e) => setIsArcheived(e.target.checked)}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon fontSize="small" />}
                onClick={openViewRoleModel}
              >
                Add New Role
              </Button>
            </Box>
          </Flex>
        )}
      </Flex>
      <Box
        marginTop={{
          sm: "130px",
          md: "120px",
          lg: "70px",
        }}
      >
        <Outlet
          context={{
            curView,
            statusKey,
            cosignerKey,
            searchKey,
            profileData,
            isArchieved,
          }}
        />
      </Box>
    </Box>
  );
};

export default UserSettingsLayout;

const STATUS_OPTIONS = [
  { value: true, label: "Active" },
  { value: false, label: "Inactive" },
];
